import React, { useState, useEffect, useCallback } from 'react';
import {
  HelpCircle, Plus, X, Check, Loader2, Trash2, Search,
  BookOpen, BarChart2, ChevronDown, TrendingUp,
  Users, FileQuestion, Star, Award, RefreshCw, PieChart, AlertTriangle, Timer, Save
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart as RePieChart, Pie, Legend
} from 'recharts';
import api from '../../api';

const DIFFICULTIES = ['easy', 'medium', 'hard'];
const TYPES = ['mcq', 'exercise', 'image', 'graph'];
const DIFF_COLOR = { easy: 'text-emerald-600 bg-emerald-50', medium: 'text-amber-600 bg-amber-50', hard: 'text-red-600 bg-red-50' };
const TYPE_COLOR = { mcq: 'text-blue-600 bg-blue-50', exercise: 'text-purple-600 bg-purple-50', image: 'text-pink-600 bg-pink-50', graph: 'text-teal-600 bg-teal-50' };

// ─── Add Question Modal ────────────────────────────────────────────────────────
function AddQuestionModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    question_text: '', subject: '', class_level: '',
    options: ['', '', '', ''], correct_index: 0,
    explanation: '', tip: '', difficulty: 'medium',
    question_type: 'mcq', exercise_context: '',
  });
  const [saving, setSaving] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [step, setStep] = useState(1); // 1=setup, 2=options

  useEffect(() => {
    api.get('/quiz/subjects').then(r => { if (r?.success) setSubjects(r.data || []); }).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.question_text.trim()) { toast.error('Question text required'); return; }
    if (!form.subject) { toast.error('Subject required'); return; }
    if (form.options.some(o => !o.trim())) { toast.error('All 4 options required'); return; }
    setSaving(true);
    try {
      const res = await api.post('/quiz/admin/questions', form);
      if (res?.success) { toast.success('Question added'); onSaved(); }
    } catch { toast.error('Failed to add question'); }
    setSaving(false);
  };

  const canProceed = form.question_text.trim() && form.subject;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="add-q-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-start justify-between flex-shrink-0">
          <div>
            <h3 className="font-black text-slate-800 text-lg">Add New Question</h3>
            <p className="text-xs text-slate-400 mt-0.5">Step {step} of 2 — {step === 1 ? 'Question setup' : 'Answer options'}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-slate-100 flex-shrink-0">
          <div className="h-full bg-[#2563eb] transition-all duration-300 rounded-full" style={{ width: `${step * 50}%` }} />
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {step === 1 ? (
            <>
              {/* Question text */}
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Question *</label>
                <textarea
                  value={form.question_text}
                  onChange={e => setForm(f => ({ ...f, question_text: e.target.value }))}
                  rows={3}
                  className="w-full border-2 border-slate-200 rounded-2xl px-4 py-3 text-sm focus:ring-0 focus:border-[#2563eb] outline-none resize-none transition-colors"
                  placeholder="Type your question here..."
                  data-testid="school-q-text"
                />
              </div>

              {/* Exercise context */}
              {form.question_type === 'exercise' && (
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Reading Passage / Context</label>
                  <textarea
                    value={form.exercise_context || ''}
                    onChange={e => setForm(f => ({ ...f, exercise_context: e.target.value }))}
                    rows={3}
                    className="w-full border-2 border-slate-200 rounded-2xl px-4 py-3 text-sm focus:border-[#2563eb] outline-none resize-none"
                    placeholder="Add reading passage that students read before answering..."
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Subject *</label>
                  <select
                    value={form.subject}
                    onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                    className="w-full border-2 border-slate-200 rounded-2xl px-4 py-2.5 text-sm focus:border-[#2563eb] outline-none bg-white"
                  >
                    <option value="">Choose subject</option>
                    {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Class Level</label>
                  <input
                    value={form.class_level}
                    onChange={e => setForm(f => ({ ...f, class_level: e.target.value }))}
                    className="w-full border-2 border-slate-200 rounded-2xl px-4 py-2.5 text-sm focus:border-[#2563eb] outline-none"
                    placeholder="e.g. JSS 2 or SS 1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Type selector */}
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Question Type</label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {TYPES.map(t => (
                      <button
                        key={t}
                        onClick={() => setForm(f => ({ ...f, question_type: t }))}
                        className={`py-1.5 px-2 rounded-xl text-xs font-semibold border-2 capitalize transition-colors ${
                          form.question_type === t
                            ? 'border-[#2563eb] bg-[#2563eb] text-white'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Difficulty */}
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Difficulty</label>
                  <div className="flex gap-1.5">
                    {DIFFICULTIES.map(d => (
                      <button
                        key={d}
                        onClick={() => setForm(f => ({ ...f, difficulty: d }))}
                        className={`flex-1 py-1.5 px-2 rounded-xl text-xs font-bold border-2 capitalize transition-colors ${
                          form.difficulty === d
                            ? d === 'easy' ? 'border-emerald-500 bg-emerald-500 text-white'
                            : d === 'medium' ? 'border-amber-500 bg-amber-500 text-white'
                            : 'border-red-500 bg-red-500 text-white'
                            : 'border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Tip */}
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Study Tip (optional)</label>
                <input
                  value={form.tip || ''}
                  onChange={e => setForm(f => ({ ...f, tip: e.target.value }))}
                  className="w-full border-2 border-slate-200 rounded-2xl px-4 py-2.5 text-sm focus:border-[#2563eb] outline-none"
                  placeholder="Hint shown to student while answering..."
                />
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-slate-500 bg-slate-50 rounded-2xl px-4 py-3 border border-slate-100 leading-relaxed">
                <span className="font-bold text-slate-700">Q:</span> {form.question_text}
              </p>
              {/* Options */}
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-3">Answer Options — click circle to mark correct answer</label>
                <div className="space-y-2.5">
                  {form.options.map((opt, i) => (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-2xl border-2 transition-colors ${
                      form.correct_index === i ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white'
                    }`}>
                      <button
                        onClick={() => setForm(f => ({ ...f, correct_index: i }))}
                        className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          form.correct_index === i ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 hover:border-emerald-400'
                        }`}
                      >
                        {form.correct_index === i && <Check className="w-3.5 h-3.5 text-white" />}
                      </button>
                      <span className={`w-6 h-6 rounded-lg text-xs font-black flex items-center justify-center flex-shrink-0 ${
                        form.correct_index === i ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {String.fromCharCode(65 + i)}
                      </span>
                      <input
                        value={opt}
                        onChange={e => { const o = [...form.options]; o[i] = e.target.value; setForm(f => ({ ...f, options: o })); }}
                        className="flex-1 bg-transparent border-none outline-none text-sm text-slate-700 placeholder-slate-400 font-medium"
                        placeholder={`Option ${String.fromCharCode(65 + i)}...`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              {/* Explanation */}
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Explanation (shown after answer)</label>
                <textarea
                  value={form.explanation || ''}
                  onChange={e => setForm(f => ({ ...f, explanation: e.target.value }))}
                  rows={2}
                  className="w-full border-2 border-slate-200 rounded-2xl px-4 py-3 text-sm focus:border-[#2563eb] outline-none resize-none"
                  placeholder="Why is this the correct answer?"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0">
          <button
            onClick={step === 1 ? onClose : () => setStep(1)}
            className="flex-1 py-2.5 rounded-2xl border-2 border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50 transition-colors"
          >
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          {step === 1 ? (
            <button
              onClick={() => setStep(2)}
              disabled={!canProceed}
              className="flex-1 py-2.5 rounded-2xl bg-[#2563eb] hover:bg-[#1d58d4] text-white text-sm font-bold disabled:opacity-40 transition-colors"
            >
              Next: Set Options →
            </button>
          ) : (
            <button
              onClick={save}
              disabled={saving || form.options.some(o => !o.trim())}
              className="flex-1 py-2.5 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-40 flex items-center justify-center gap-2 transition-colors"
              data-testid="school-save-q-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save Question
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, color, bg }) {
  return (
    <div className={`${bg} rounded-2xl p-4 flex items-center gap-3`}>
      <div className={`w-10 h-10 rounded-xl ${color} bg-white/60 flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className={`text-xl font-black ${color}`}>{value}</p>
        <p className="text-xs text-slate-500 font-medium mt-0.5">{label}</p>
      </div>
    </div>
  );
}

// ─── Question Row ──────────────────────────────────────────────────────────────
function QuestionRow({ q, onDelete, deleting }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`border-b border-slate-50 last:border-0 transition-colors ${expanded ? 'bg-slate-50/50' : 'hover:bg-slate-50/80'}`}
      data-testid={`sq-row-${q.id}`}>
      <div
        className="flex items-start gap-3 px-5 py-3.5 cursor-pointer"
        onClick={() => setExpanded(e => !e)}
      >
        {/* Number badge */}
        <div className="w-6 h-6 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <FileQuestion className="w-3.5 h-3.5 text-slate-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-700 font-medium line-clamp-2 leading-snug">{q.question_text}</p>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {q.subject && (
              <span className="text-[10px] font-bold bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                {q.subject}
              </span>
            )}
            {q.class_level && (
              <span className="text-[10px] font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                {q.class_level}
              </span>
            )}
            {q.difficulty && (
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${DIFF_COLOR[q.difficulty] || 'text-slate-500 bg-slate-100'}`}>
                {q.difficulty}
              </span>
            )}
            {q.question_type && q.question_type !== 'mcq' && (
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${TYPE_COLOR[q.question_type] || 'text-slate-500 bg-slate-100'}`}>
                {q.question_type}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <ChevronDown className={`w-4 h-4 text-slate-300 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          <button
            onClick={e => { e.stopPropagation(); onDelete(q.id); }}
            disabled={deleting === q.id}
            className="p-1.5 rounded-xl text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            {deleting === q.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      {/* Expanded options */}
      {expanded && (
        <div className="px-5 pb-4 space-y-1.5">
          {(q.options || []).map((opt, i) => (
            <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs ${i === q.correct_index ? 'bg-emerald-50 border border-emerald-200' : 'bg-white border border-slate-100'}`}>
              <span className={`w-5 h-5 rounded-md text-[10px] font-black flex items-center justify-center flex-shrink-0 ${i === q.correct_index ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                {String.fromCharCode(65 + i)}
              </span>
              <span className={i === q.correct_index ? 'text-emerald-700 font-semibold' : 'text-slate-600'}>{opt}</span>
              {i === q.correct_index && <Check className="w-3 h-3 text-emerald-500 ml-auto" />}
            </div>
          ))}
          {q.explanation && (
            <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
              <span className="font-bold">Explanation:</span> {q.explanation}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function SchoolQuizManagementPage() {
  const [tab, setTab] = useState('questions');
  const [questions, setQuestions] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [timerConfig, setTimerConfig] = useState({ subject_timers: {}, default_timer: 75 });
  const [timerDraft, setTimerDraft]   = useState({ subject_timers: {}, default_timer: 75 });
  const [savingTimer, setSavingTimer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterDiff, setFilterDiff] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [deleting, setDeleting] = useState('');
  const [total, setTotal] = useState(0);
  const [subjects, setSubjects] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, pRes, sRes, aRes, tRes] = await Promise.allSettled([
        api.get(`/quiz/admin/questions?q=${encodeURIComponent(search)}`),
        api.get('/quiz/admin/performance'),
        api.get('/quiz/subjects'),
        api.get('/quiz/admin/analytics'),
        api.get('/quiz/admin/timer-config'),
      ]);
      if (qRes.status === 'fulfilled' && qRes.value?.success) {
        setQuestions(qRes.value.data || []);
        setTotal(qRes.value.total || (qRes.value.data || []).length);
      }
      if (pRes.status === 'fulfilled' && pRes.value?.success) setPerformance(pRes.value.data || []);
      if (sRes.status === 'fulfilled' && sRes.value?.success) setSubjects(sRes.value.data || []);
      if (aRes.status === 'fulfilled' && aRes.value?.success) setAnalytics(aRes.value.data);
      if (tRes.status === 'fulfilled' && tRes.value?.success) {
        const cfg = tRes.value.data || { subject_timers: {}, default_timer: 75 };
        setTimerConfig(cfg);
        setTimerDraft(JSON.parse(JSON.stringify(cfg)));
      }
    } catch {}
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    setDeleting(id);
    try {
      const res = await api.delete(`/quiz/admin/questions/${id}`);
      if (res?.success) { toast.success('Deleted'); load(); }
    } catch { toast.error('Failed to delete'); }
    setDeleting('');
  };

  // Apply client-side filters
  const filtered = questions.filter(q => {
    if (filterSubject && q.subject !== filterSubject) return false;
    if (filterDiff && q.difficulty !== filterDiff) return false;
    return true;
  });

  const stats = {
    totalQ: total,
    subjects: [...new Set(questions.map(q => q.subject).filter(Boolean))].length,
    easy: questions.filter(q => q.difficulty === 'easy').length,
    hard: questions.filter(q => q.difficulty === 'hard').length,
  };

  const avgPerf = performance.length
    ? Math.round(performance.reduce((s, p) => s + (p.avg_pct || 0), 0) / performance.length)
    : 0;

  return (
    <div className="flex flex-col h-full bg-slate-50 min-h-screen">

      {/* ── Top Header ──────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-100 px-6 py-5 flex-shrink-0">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-pink-100 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-800" data-testid="school-quiz-title">Quiz Management</h1>
              <p className="text-xs text-slate-400">{total.toLocaleString()} school questions · {subjects.length} subjects</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="p-2.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d58d4] text-white font-bold px-5 py-2.5 rounded-xl shadow-sm transition-colors text-sm"
              data-testid="school-add-q-btn"
            >
              <Plus className="w-4 h-4" /> Add Question
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto px-6 py-5 space-y-5">

          {/* ── Stats Row ─────────────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="Total Questions" value={stats.totalQ.toLocaleString()} icon={FileQuestion} color="text-[#2563eb]" bg="bg-blue-50" />
            <StatCard label="Subjects Covered" value={stats.subjects} icon={BookOpen} color="text-purple-600" bg="bg-purple-50" />
            <StatCard label="Students Quizzed" value={performance.length} icon={Users} color="text-emerald-600" bg="bg-emerald-50" />
            <StatCard label="Avg Score" value={`${avgPerf}%`} icon={TrendingUp} color="text-amber-600" bg="bg-amber-50" />
          </div>

          {/* ── Nav Tabs ──────────────────────────────────────────────────────── */}
          <div className="flex border-b border-slate-200 gap-0">
            {[
              { key: 'questions',    label: 'Questions',          icon: BookOpen,   count: filtered.length },
              { key: 'performance',  label: 'Student Performance', icon: BarChart2,  count: performance.length },
              { key: 'analytics',    label: 'Analytics',           icon: PieChart,   count: null },
              { key: 'timer',        label: 'Timer Config',        icon: Timer,      count: null },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-bold border-b-2 transition-colors ${
                  tab === t.key
                    ? 'border-[#2563eb] text-[#2563eb]'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
                data-testid={`tab-school-${t.key}`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
                {t.count !== null && (
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                    tab === t.key ? 'bg-blue-100 text-[#2563eb]' : 'bg-slate-100 text-slate-400'
                  }`}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* ── Questions Tab ─────────────────────────────────────────────────── */}
          {tab === 'questions' && (
            <>
              {/* Filter Bar */}
              <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition"
                    placeholder="Search questions..."
                    data-testid="school-q-search"
                  />
                </div>
                <select
                  value={filterSubject}
                  onChange={e => setFilterSubject(e.target.value)}
                  className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none text-slate-600"
                >
                  <option value="">All Subjects</option>
                  {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="flex gap-1.5">
                  {['', ...DIFFICULTIES].map(d => (
                    <button
                      key={d}
                      onClick={() => setFilterDiff(d)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-colors capitalize ${
                        filterDiff === d
                          ? d === 'easy' ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
                          : d === 'medium' ? 'bg-amber-100 border-amber-300 text-amber-700'
                          : d === 'hard' ? 'bg-red-100 border-red-300 text-red-700'
                          : 'bg-slate-800 border-transparent text-white'
                          : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      {d || 'All'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Questions List */}
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
                      <p className="text-xs text-slate-400">Loading questions...</p>
                    </div>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="py-20 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-3xl flex items-center justify-center mx-auto mb-4">
                      <HelpCircle className="w-8 h-8 text-slate-300" />
                    </div>
                    <p className="font-bold text-slate-500">No questions yet</p>
                    <p className="text-sm text-slate-400 mt-1 mb-4">
                      {search || filterSubject || filterDiff
                        ? 'Try clearing your filters'
                        : 'Students also access 1,000+ EDUVA-wide questions'
                      }
                    </p>
                    {!search && !filterSubject && !filterDiff && (
                      <button
                        onClick={() => setShowAdd(true)}
                        className="mx-auto flex items-center gap-2 bg-[#2563eb] text-white text-sm font-bold px-5 py-2.5 rounded-xl"
                      >
                        <Plus className="w-4 h-4" /> Add First Question
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="px-5 py-3 border-b border-slate-50 flex items-center justify-between">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">
                        Showing {filtered.length} question{filtered.length !== 1 ? 's' : ''}
                        {(filterSubject || filterDiff) && ' (filtered)'}
                      </p>
                      {(filterSubject || filterDiff) && (
                        <button
                          onClick={() => { setFilterSubject(''); setFilterDiff(''); }}
                          className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
                        >
                          <X className="w-3 h-3" /> Clear filters
                        </button>
                      )}
                    </div>
                    {filtered.map(q => (
                      <QuestionRow key={q.id} q={q} onDelete={del} deleting={deleting} />
                    ))}
                  </>
                )}
              </div>
            </>
          )}

          {/* ── Performance Tab ───────────────────────────────────────────────── */}
          {tab === 'performance' && (
            <div className="space-y-4">
              {/* Quick performance stats */}
              {performance.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {[
                    {
                      label: 'Average Score',
                      value: `${avgPerf}%`,
                      color: avgPerf >= 70 ? 'text-emerald-600 bg-emerald-50' : avgPerf >= 50 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50',
                    },
                    {
                      label: 'Total Attempts',
                      value: performance.reduce((s, p) => s + (p.total_attempts || 0), 0),
                      color: 'text-blue-600 bg-blue-50',
                    },
                    {
                      label: 'Students Active',
                      value: performance.length,
                      color: 'text-purple-600 bg-purple-50',
                    },
                  ].map(s => (
                    <div key={s.label} className={`${s.color} rounded-2xl p-4 text-center`}>
                      <p className="text-2xl font-black">{s.value}</p>
                      <p className="text-xs font-medium text-slate-500 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-slate-50 flex items-center justify-between">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Student Performance</p>
                  {performance.length > 0 && (
                    <span className="text-xs text-slate-400">{performance.length} students</span>
                  )}
                </div>
                {loading ? (
                  <div className="flex justify-center py-16">
                    <div className="w-7 h-7 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : performance.length === 0 ? (
                  <div className="py-16 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-3xl flex items-center justify-center mx-auto mb-4">
                      <BarChart2 className="w-8 h-8 text-slate-300" />
                    </div>
                    <p className="font-bold text-slate-500">No quiz attempts yet</p>
                    <p className="text-sm text-slate-400 mt-1">Data will appear once students take quizzes</p>
                  </div>
                ) : (
                  <>
                    {/* Header row */}
                    <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-slate-50 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      <span className="col-span-1">#</span>
                      <span className="col-span-4">Student</span>
                      <span className="col-span-3">Class</span>
                      <span className="col-span-2 text-center">Attempts</span>
                      <span className="col-span-2 text-center">Best</span>
                    </div>
                    {performance.map((p, i) => {
                      const scoreColor = p.best_pct >= 80 ? 'text-emerald-600 bg-emerald-50' : p.best_pct >= 60 ? 'text-blue-600 bg-blue-50' : 'text-amber-600 bg-amber-50';
                      return (
                        <div key={i} className="grid grid-cols-12 gap-2 px-5 py-3.5 border-t border-slate-50 hover:bg-slate-50/50 items-center">
                          <span className="col-span-1 text-xs text-slate-300 font-bold">{i + 1}</span>
                          <div className="col-span-4 flex items-center gap-2">
                            <div className="w-7 h-7 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] font-black text-[#2563eb]">{p.student_name?.charAt(0)}</span>
                            </div>
                            <p className="text-sm font-semibold text-slate-700 truncate">{p.student_name}</p>
                          </div>
                          <span className="col-span-3 text-xs text-slate-400">{p.class_name || '—'}</span>
                          <span className="col-span-2 text-center text-sm font-bold text-slate-600">{p.total_attempts}</span>
                          <div className="col-span-2 flex justify-center">
                            <span className={`text-xs font-black px-2.5 py-1 rounded-xl ${scoreColor}`}>{p.best_pct}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Analytics Tab ─────────────────────────────────────────────────── */}
          {tab === 'analytics' && (
            <div className="space-y-5" data-testid="quiz-analytics-tab">
              {!analytics ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : analytics.total_attempts === 0 ? (
                <div className="bg-white rounded-3xl border border-slate-100 shadow-sm py-20 text-center">
                  <BarChart2 className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                  <p className="font-bold text-slate-500">No quiz data yet</p>
                  <p className="text-sm text-slate-400 mt-1">Analytics will appear once students take quizzes</p>
                </div>
              ) : (
                <>
                  {/* Top metrics */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-50 rounded-2xl p-4 text-center">
                      <p className="text-2xl font-black text-[#2563eb]">{analytics.total_attempts}</p>
                      <p className="text-xs text-slate-500 mt-0.5">Total Attempts</p>
                    </div>
                    <div className="bg-emerald-50 rounded-2xl p-4 text-center">
                      <p className="text-2xl font-black text-emerald-600">{analytics.by_subject.length}</p>
                      <p className="text-xs text-slate-500 mt-0.5">Subjects Covered</p>
                    </div>
                    <div className={`rounded-2xl p-4 text-center ${
                      analytics.by_subject.length > 0 && analytics.by_subject[0].avg_pct < 50
                        ? 'bg-red-50' : 'bg-amber-50'
                    }`}>
                      <p className={`text-2xl font-black ${
                        analytics.by_subject.length > 0 && analytics.by_subject[0].avg_pct < 50
                          ? 'text-red-600' : 'text-amber-600'
                      }`}>
                        {analytics.by_subject.length > 0 ? analytics.by_subject[0].subject : '—'}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">Most Struggled Subject</p>
                    </div>
                  </div>

                  {/* Subject Performance Chart */}
                  <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5" data-testid="subject-performance-chart">
                    <div className="flex items-center gap-2 mb-4">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <h3 className="font-black text-slate-800 text-sm">Subject Performance — Average Score</h3>
                    </div>
                    <p className="text-xs text-slate-400 mb-4">Sorted by average score (lowest first — most struggling subjects)</p>
                    <ResponsiveContainer width="100%" height={Math.max(200, analytics.by_subject.length * 42)}>
                      <BarChart
                        data={analytics.by_subject.map(s => ({ name: s.subject, avg: s.avg_pct, attempts: s.attempts }))}
                        layout="vertical"
                        margin={{ top: 0, right: 20, left: 10, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                        <Tooltip
                          formatter={(val, name, props) => [`${val}% avg (${props.payload.attempts} attempts)`, 'Score']}
                          contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
                        />
                        <Bar dataKey="avg" radius={[0, 6, 6, 0]} maxBarSize={28}>
                          {analytics.by_subject.map((s, i) => (
                            <Cell
                              key={i}
                              fill={s.avg_pct >= 70 ? '#10b981' : s.avg_pct >= 50 ? '#f59e0b' : '#ef4444'}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    {/* Legend */}
                    <div className="flex gap-4 mt-3 justify-center">
                      {[['#10b981','≥70% Good'],['#f59e0b','50–69% Fair'],['#ef4444','<50% Struggling']].map(([c,l]) => (
                        <div key={l} className="flex items-center gap-1.5">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c }} />
                          <span className="text-xs text-slate-500">{l}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Two-column: Class Perf + Difficulty */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {/* Class Performance */}
                    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5" data-testid="class-performance-chart">
                      <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="w-4 h-4 text-[#2563eb]" />
                        <h3 className="font-black text-slate-800 text-sm">Class Average Score</h3>
                      </div>
                      {analytics.by_class.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-8">No class data yet</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={Math.max(160, analytics.by_class.length * 44)}>
                          <BarChart
                            data={analytics.by_class.map(c => ({ name: c.class_name, avg: c.avg_pct, attempts: c.attempts }))}
                            layout="vertical"
                            margin={{ top: 0, right: 20, left: 10, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={70} />
                            <Tooltip
                              formatter={(val, name, props) => [`${val}% (${props.payload.attempts} attempts)`, 'Avg']}
                              contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
                            />
                            <Bar dataKey="avg" radius={[0, 6, 6, 0]} maxBarSize={26} fill="#2563eb" />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>

                    {/* Difficulty Distribution */}
                    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5" data-testid="difficulty-chart">
                      <div className="flex items-center gap-2 mb-4">
                        <Star className="w-4 h-4 text-amber-500" />
                        <h3 className="font-black text-slate-800 text-sm">Question Difficulty Mix</h3>
                      </div>
                      {(analytics.difficulty.easy + analytics.difficulty.medium + analytics.difficulty.hard) === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-8">No school questions added yet</p>
                      ) : (
                        <>
                          <ResponsiveContainer width="100%" height={180}>
                            <RePieChart>
                              <Pie
                                data={[
                                  { name: 'Easy', value: analytics.difficulty.easy, fill: '#10b981' },
                                  { name: 'Medium', value: analytics.difficulty.medium, fill: '#f59e0b' },
                                  { name: 'Hard', value: analytics.difficulty.hard, fill: '#ef4444' },
                                ].filter(d => d.value > 0)}
                                cx="50%" cy="50%" outerRadius={70} innerRadius={35}
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                              >
                              </Pie>
                              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                            </RePieChart>
                          </ResponsiveContainer>
                          <div className="grid grid-cols-3 gap-2 mt-2">
                            {[
                              { label: 'Easy', value: analytics.difficulty.easy, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                              { label: 'Medium', value: analytics.difficulty.medium, color: 'text-amber-600', bg: 'bg-amber-50' },
                              { label: 'Hard', value: analytics.difficulty.hard, color: 'text-red-600', bg: 'bg-red-50' },
                            ].map(d => (
                              <div key={d.label} className={`${d.bg} rounded-xl p-2.5 text-center`}>
                                <p className={`text-xl font-black ${d.color}`}>{d.value}</p>
                                <p className="text-[10px] text-slate-500 mt-0.5">{d.label}</p>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {/* ── Timer Config Tab ──────────────────────────────────────────── */}
          {tab === 'timer' && (
            <div className="space-y-5" data-testid="quiz-timer-tab">
              <div className="bg-blue-50 border border-blue-200 rounded-2xl px-5 py-3.5 flex items-start gap-3">
                <Timer className="w-5 h-5 text-[#2563eb] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-[#2563eb]">Per-Subject Quiz Timers</p>
                  <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                    Set how many seconds students have per question for each subject.
                    The default applies to any subject not listed here.
                    These limits will be applied live in students' Quiz Zone sessions.
                  </p>
                </div>
              </div>

              {/* Default timer */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <p className="text-sm font-black text-slate-700 mb-3">Default Timer</p>
                <div className="flex items-center gap-3 max-w-xs">
                  <div className="flex-1 relative">
                    <input
                      type="number"
                      min={15} max={300}
                      value={timerDraft.default_timer}
                      onChange={e => setTimerDraft(d => ({ ...d, default_timer: Math.max(15, Math.min(300, +e.target.value)) }))}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      data-testid="default-timer-input"
                    />
                    <span className="absolute right-3 top-2 text-xs text-slate-400">sec</span>
                  </div>
                  <span className="text-xs text-slate-400">{Math.floor(timerDraft.default_timer / 60)}m {timerDraft.default_timer % 60}s per question</span>
                </div>
              </div>

              {/* Per-subject overrides */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-50 flex items-center justify-between">
                  <p className="text-sm font-black text-slate-700">Subject Overrides</p>
                  <span className="text-xs text-slate-400">Leave empty to use default</span>
                </div>
                <div className="divide-y divide-slate-50">
                  {[
                    'Mathematics','English Language','Physics','Chemistry','Biology',
                    'Economics','Geography','Government','Literature','Agricultural Science',
                    'Civic Education','Computer Studies','Basic Science','Further Mathematics',
                  ].map(subj => {
                    const val = timerDraft.subject_timers[subj];
                    return (
                      <div key={subj} className="flex items-center gap-3 px-5 py-3" data-testid={`timer-row-${subj.replace(/\s+/g,'-').toLowerCase()}`}>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-slate-700">{subj}</p>
                          <p className="text-[10px] text-slate-400">
                            {val ? `${val}s per question` : `Default (${timerDraft.default_timer}s)`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={15} max={300}
                            placeholder={String(timerDraft.default_timer)}
                            value={val || ''}
                            onChange={e => {
                              const n = e.target.value === '' ? undefined : Math.max(15, Math.min(300, +e.target.value));
                              setTimerDraft(d => {
                                const t = { ...d.subject_timers };
                                if (n === undefined) delete t[subj]; else t[subj] = n;
                                return { ...d, subject_timers: t };
                              });
                            }}
                            className="w-20 border border-slate-200 rounded-xl px-2 py-1.5 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 text-center"
                          />
                          <span className="text-xs text-slate-400 w-6">s</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Save */}
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    setSavingTimer(true);
                    const res = await api.put('/quiz/admin/timer-config', timerDraft);
                    setSavingTimer(false);
                    if (res?.success) {
                      setTimerConfig(JSON.parse(JSON.stringify(timerDraft)));
                      toast.success('Timer settings saved successfully');
                    } else { toast.error('Failed to save timer settings'); }
                  }}
                  disabled={savingTimer}
                  className="flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d58d4] disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-xl shadow-sm transition-colors"
                  data-testid="save-timer-config-btn"
                >
                  {savingTimer ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Timer Settings
                </button>
                <button
                  onClick={() => setTimerDraft(JSON.parse(JSON.stringify(timerConfig)))}
                  className="text-sm text-slate-500 hover:text-slate-700 font-medium"
                >
                  Reset Changes
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showAdd && (
        <AddQuestionModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />
      )}
    </div>
  );
}
